"use client"

import { Button } from "@/components/ui/Button"
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import type { ThemeRoute } from "@/utils/themeColors"
import { getThemeHeaderBackground } from "@/utils/themeColors"
import { Info } from "lucide-react"
import { useState } from "react"

interface ToolboxDetailLayoutProps {
  children: React.ReactNode
  sidebar: React.ReactNode | null
  title: string
  breadcrumbs?: Array<{ label: string; href?: string }>
  backHref?: string
  className?: string
  headerActions?: React.ReactNode
  themeColor?: ThemeRoute | null
}

export default function ToolboxDetailLayout({
  children,
  sidebar,
  title,
  breadcrumbs,
  backHref = "/",
  className,
  headerActions,
  themeColor,
}: ToolboxDetailLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  // Validate title is a string to prevent React error #418
  const sanitizedTitle =
    typeof title === "string" ? title : title ? String(title) : "Untitled"

  // Validate breadcrumbs labels are strings
  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => ({
    ...breadcrumb,
    label:
      typeof breadcrumb.label === "string"
        ? breadcrumb.label
        : breadcrumb.label
          ? String(breadcrumb.label)
          : "Untitled",
  }))

  const headerBgClass = themeColor
    ? getThemeHeaderBackground(themeColor)
    : "bg-background/95"

  return (
    <div className={cn("min-h-screen w-full bg-background", className)}>
      {/* Full-width header with theme color */}
      <header
        className={cn(
          "lg:sticky lg:top-21 z-20 w-full border-b backdrop-blur supports-backdrop-filter:bg-background/60 lg:px-8",
          headerBgClass
        )}
      >
        <div className="mx-auto px-4 py-4">
          {sanitizedBreadcrumbs && sanitizedBreadcrumbs.length > 0 && (
            <nav
              className="mb-2 text-sm text-muted-foreground"
              aria-label="Breadcrumb"
            >
              {sanitizedBreadcrumbs.map((crumb, index) => (
                <span key={index}>
                  {crumb.href ? (
                    <a
                      href={crumb.href}
                      className="hover:text-foreground transition-colors"
                    >
                      {crumb.label}
                    </a>
                  ) : (
                    <span>{crumb.label}</span>
                  )}
                  {index < sanitizedBreadcrumbs.length - 1 && (
                    <span className="mx-2">/</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold text-foreground md:text-3xl">
              {sanitizedTitle}
            </h1>
            <div className="flex items-center gap-3">
              {/* Header actions (bookmarks, etc.) */}
              {headerActions && (
                <div className="flex items-center gap-2 mr-2">
                  {headerActions}
                </div>
              )}

              {/* Mobile sidebar button - only show if sidebar exists */}
              {sidebar && (
                <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                  <SheetTitle className="sr-only">Item Details</SheetTitle>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="lg:hidden"
                      aria-label="Toggle details sidebar"
                    >
                      <Info className="h-4 w-4" />
                      <span className="sr-only">Details</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="left"
                    className="w-[85vw] max-w-sm overflow-y-auto p-0"
                  >
                    <div className="p-4">{sidebar}</div>
                  </SheetContent>
                </Sheet>
              )}
              {backHref && (
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="hidden sm:flex"
                >
                  <a href={backHref}>← Back</a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <div className="px-4 py-6">
        <div className="flex gap-6">
          {/* Desktop sidebar - hidden on mobile, only show if sidebar exists */}
          {sidebar && (
            <aside
              className="hidden lg:block w-80 shrink-0"
              role="complementary"
              aria-label="Item details"
            >
              <div className="sticky top-46">{sidebar}</div>
            </aside>
          )}

          {/* Main content */}
          <main className="flex-1 min-w-0" role="main" id="main-content">
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
